/**
 * Expands base seed arrays to MOCK_SEED_MULTIPLIER × size while preserving
 * cross-references (thread_id, board_id, etc.) across copies.
 */

import { encodeGeohash, toRFC3339UTC } from './geohashUtil';
import {
  anchorForCopy,
  jitterAround,
  geoBoardTheme,
  globalBoardName,
  EXTRA_TOPIC_TAGS,
} from './worldLocations';
import { SEED_THREADS } from './seedThreads';
import { SEED_COMMENTS } from './seedComments';
import { SEED_LIKES } from './seedLikes';
import { SEED_REPORTS } from './seedReports';
import { SEED_VIEW_DEDUP } from './seedViewDedup';
import { SEED_VOTES } from './seedVotes';
import { SEED_TAGS } from './seedTags';
import { SEED_BOARDS } from './seedBoards';
import { SEED_USER_SETTINGS } from './seedUserSettings';

export const MOCK_SEED_MULTIPLIER = 100;

function clone(obj) {
  return JSON.parse(JSON.stringify(obj));
}

function shiftTime(iso, copyIndex, extraMs = 0) {
  const base = new Date(iso).getTime();
  return toRFC3339UTC(new Date(base - copyIndex * 86400000 - extraMs));
}

function remapId(id, copyIndex, baseCount) {
  return id + copyIndex * baseCount;
}

function relocateRegional(row, copyIndex, slotSeed) {
  if (row.latitude == null || row.longitude == null) return;
  const anchor = anchorForCopy(copyIndex, slotSeed);
  const { lat, lon } = jitterAround(anchor.lat, anchor.lon, slotSeed + copyIndex * 17);
  row.latitude = lat;
  row.longitude = lon;
  row.geohash = encodeGeohash(lat, lon);
  if (anchor.planet_id != null) row.planet_id = anchor.planet_id;
}

function expandThreads(base, multiplier) {
  const baseCount = base.length;
  const out = [];
  for (let copy = 0; copy < multiplier; copy += 1) {
    for (const t of base) {
      const row = clone(t);
      row.id = remapId(t.id, copy, baseCount);
      if (copy > 0 && row.title) {
        row.title = `${row.title} ·${copy + 1}`;
      }
      if (row.body && copy > 0) {
        row.body = `${row.body} (copy ${copy + 1})`;
      }
      if (row.latitude != null && row.longitude != null) {
        if (copy > 0) {
          relocateRegional(row, copy, t.id);
        } else {
          const jitter = (t.id % 7) * 0.0004;
          row.latitude = Number((row.latitude + jitter * Math.cos(t.id)).toFixed(6));
          row.longitude = Number((row.longitude + jitter * Math.sin(t.id)).toFixed(6));
          row.geohash = encodeGeohash(row.latitude, row.longitude);
        }
      }
      row.created_at = shiftTime(t.created_at, copy);
      row.updated_at = row.created_at;
      row.edited_at = t.edited_at ? shiftTime(t.edited_at, copy) : null;
      out.push(row);
    }
  }
  return out;
}

function expandComments(base, multiplier, baseThreadCount) {
  const baseCount = base.length;
  const out = [];
  for (let copy = 0; copy < multiplier; copy += 1) {
    for (const c of base) {
      const row = clone(c);
      row.id = remapId(c.id, copy, baseCount);
      row.thread_id = remapId(c.thread_id, copy, baseThreadCount);
      row.created_at = shiftTime(c.created_at, copy, c.id * 60000);
      row.updated_at = row.created_at;
      row.edited_at = c.edited_at ? shiftTime(c.edited_at, copy) : null;
      if (copy > 0) row.body = `${c.body} (#${copy + 1})`;
      out.push(row);
    }
  }
  return out;
}

function expandVotes(base, multiplier, baseThreadCount) {
  const out = [];
  for (let copy = 0; copy < multiplier; copy += 1) {
    for (const v of base) {
      out.push({
        ...clone(v),
        thread_id: remapId(v.thread_id, copy, baseThreadCount),
        created_at: shiftTime(v.created_at, copy),
      });
    }
  }
  return out;
}

function expandLikes(base, multiplier, baseThreadCount) {
  const out = [];
  for (let copy = 0; copy < multiplier; copy += 1) {
    for (const l of base) {
      out.push({
        ...clone(l),
        thread_id: remapId(l.thread_id, copy, baseThreadCount),
        created_at: shiftTime(l.created_at, copy),
      });
    }
  }
  return out;
}

function expandReports(base, multiplier, baseThreadCount) {
  const baseCount = base.length;
  const out = [];
  for (let copy = 0; copy < multiplier; copy += 1) {
    for (const r of base) {
      out.push({
        ...clone(r),
        id: remapId(r.id, copy, baseCount),
        thread_id: remapId(r.thread_id, copy, baseThreadCount),
        created_at: shiftTime(r.created_at, copy),
      });
    }
  }
  return out;
}

function expandTags(base, multiplier, baseThreadCount) {
  const seen = new Set();
  const push = (out, threadId, tag) => {
    const key = `${threadId}:${tag}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ thread_id: threadId, tag });
  };

  const out = [];
  for (let copy = 0; copy < multiplier; copy += 1) {
    for (const tag of base) {
      push(out, remapId(tag.thread_id, copy, baseThreadCount), tag.tag);
    }
    if (copy === 0) continue;
    for (const t of SEED_THREADS) {
      if (t.kind !== 'regional') continue;
      const threadId = remapId(t.id, copy, baseThreadCount);
      const anchor = anchorForCopy(copy, t.id);
      push(out, threadId, anchor.cityTag);
      push(out, threadId, anchor.regionTag);
      push(out, threadId, EXTRA_TOPIC_TAGS[(copy + t.id) % EXTRA_TOPIC_TAGS.length]);
    }
  }
  return out;
}

function expandViewDedup(base, multiplier, baseThreadCount) {
  const out = [];
  for (let copy = 0; copy < multiplier; copy += 1) {
    for (const v of base) {
      out.push({
        thread_id: remapId(v.thread_id, copy, baseThreadCount),
        user_id: v.user_id + copy * 10,
        bucket_date: v.bucket_date,
      });
    }
  }
  return out;
}

function expandBoards(base, multiplier, baseThreadCount) {
  const baseCount = base.length;
  const out = [];
  for (let copy = 0; copy < multiplier; copy += 1) {
    for (const b of base) {
      const row = clone(b);
      row.id = remapId(b.id, copy, baseCount);
      if (row.latitude != null && row.longitude != null) {
        if (copy > 0) {
          const anchor = anchorForCopy(copy, b.id);
          const theme = geoBoardTheme(b.id, copy);
          row.name = `${anchor.slug}-${theme}`;
          row.board_key = row.name.toLowerCase();
          const { lat, lon } = jitterAround(anchor.lat, anchor.lon, b.id + copy * 13, 0.05);
          row.latitude = lat;
          row.longitude = lon;
          row.geohash = encodeGeohash(lat, lon);
          if (anchor.planet_id != null) row.planet_id = anchor.planet_id;
        }
      } else if (copy > 0) {
        row.name = globalBoardName(copy, b.id, baseCount);
        row.board_key = row.name.toLowerCase();
      }
      row.thread_ids = (b.thread_ids || []).map((tid) => remapId(tid, copy, baseThreadCount));
      row.created_at = shiftTime(b.created_at, copy);
      out.push(row);
    }
  }
  return out;
}

function expandUserSettings(base, multiplier) {
  const entries = Object.entries(base);
  const out = {};
  for (let copy = 0; copy < multiplier; copy += 1) {
    for (const [uid, dist] of entries) {
      out[Number(uid) + copy * 100] = dist;
    }
  }
  return out;
}

let cached = null;

/** Returns all mock seed collections expanded to MOCK_SEED_MULTIPLIER × base size. */
export function getExpandedMockSeeds(multiplier = MOCK_SEED_MULTIPLIER) {
  if (cached && cached._multiplier === multiplier) return cached;

  const baseThreadCount = SEED_THREADS.length;
  const threads = expandThreads(SEED_THREADS, multiplier);
  const comments = expandComments(SEED_COMMENTS, multiplier, baseThreadCount);
  const votes = expandVotes(SEED_VOTES, multiplier, baseThreadCount);
  const likes = expandLikes(SEED_LIKES, multiplier, baseThreadCount);
  const reports = expandReports(SEED_REPORTS, multiplier, baseThreadCount);
  const threadTags = expandTags(SEED_TAGS, multiplier, baseThreadCount);
  const viewDedup = expandViewDedup(SEED_VIEW_DEDUP, multiplier, baseThreadCount);
  const boards = expandBoards(SEED_BOARDS, multiplier, baseThreadCount);
  const userSettings = expandUserSettings(SEED_USER_SETTINGS, multiplier);

  cached = {
    _multiplier: multiplier,
    threads,
    comments,
    votes,
    likes,
    reports,
    threadTags,
    viewDedup,
    boards,
    userSettings,
    counts: {
      threads: threads.length,
      comments: comments.length,
      votes: votes.length,
      likes: likes.length,
      reports: reports.length,
      tags: threadTags.length,
      boards: boards.length,
    },
  };
  return cached;
}

/** Clear expansion cache (e.g. after tests tweak multiplier). */
export function clearExpandedMockSeedCache() {
  cached = null;
}
